import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Form, Input, Spin, Typography } from "antd";

import Base from "../Base";
import { Locale, ApiManager } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { setUserMerchantLocation } from "../../utils/redux/actions/User";
import {
  DEFAULT_MAP_CENTER,
  createClickableMarkerMap,
  parseLngLat,
} from "../../utils/tianditu";
import { TIANDITU_ZOOM } from "../../constants";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const base = Base.getInstance();
const { Paragraph } = Typography;

function MerchantLocationSetting() {
  const user = useSelector((state) => getUserInfo(state));
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const mapInstance = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const merchant = user?.merchant;

  useEffect(() => {
    if (!merchant) return undefined;

    const longitude = parseFloat(merchant.longitude);
    const latitude = parseFloat(merchant.latitude);
    const hasLocation =
      !Number.isNaN(longitude) &&
      !Number.isNaN(latitude) &&
      longitude !== 0 &&
      latitude !== 0;

    const center = hasLocation
      ? { longitude, latitude }
      : DEFAULT_MAP_CENTER;

    form.setFieldsValue({
      coordinate: `${center.longitude},${center.latitude}`,
    });

    let cancelled = false;
    setLoading(true);
    createClickableMarkerMap({
      containerId: "merchant-location-map",
      longitude: center.longitude,
      latitude: center.latitude,
      zoom: TIANDITU_ZOOM,
      onPositionChange: ({ longitude: lng, latitude: lat }) => {
        form.setFieldsValue({
          coordinate: `${lng},${lat}`,
        });
      },
    })
      .then((instance) => {
        if (cancelled) {
          instance.destroy?.();
          return;
        }
        mapInstance.current = instance;
        instance.resize();
      })
      .catch((error) => {
        console.error("MerchantLocationSetting map init failed", error);
        base.showMessage(
          i18n.t("merchantLocation.mapLoadFailed"),
          Base.Message.error
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      mapInstance.current?.destroy?.();
      mapInstance.current = null;
    };
  }, [merchant?.id, form]);

  const onCoordinateBlur = () => {
    const parsed = parseLngLat(form.getFieldValue("coordinate"));
    if (!parsed || !mapInstance.current?.marker || !window.T) return;
    const { longitude, latitude } = parsed;
    mapInstance.current.marker.setLngLat(
      new window.T.LngLat(longitude, latitude)
    );
    mapInstance.current.map.panTo(new window.T.LngLat(longitude, latitude));
  };

  const handleSubmit = async (values) => {
    const token = user?.token;
    if (!token || !merchant?.id) return;

    const parsed = parseLngLat(values.coordinate);
    if (!parsed) {
      base.showMessage(
        i18n.t("merchantLocation.coordinateHint"),
        Base.Message.error
      );
      return;
    }

    setSaving(true);
    try {
      const updateApi = api.merchant.updateMerchantBasicInfo;
      const res = await updateApi.run({
        token,
        path: { id: merchant.id },
        data: {
          longitude: parsed.longitude,
          latitude: parsed.latitude,
        },
      });
      if (res.result) {
        dispatch(
          setUserMerchantLocation(parsed.longitude, parsed.latitude)
        );
        base.showMessage(res.message, Base.Message.success);
      } else {
        base.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      base.handleApiError(api.merchant.updateMerchantBasicInfo, error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading || saving}>
      <Card title={i18n.t("merchantLocation.title")}>
        <Paragraph type="secondary">
          {i18n.t("merchantLocation.pageHint")}
        </Paragraph>
        <Form
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 12 }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label={i18n.t("merchantLocation.coordinate")}
            name="coordinate"
            rules={[
              {
                required: true,
                message: i18n.t("merchantLocation.coordinateHint"),
              },
              {
                validator: (_, value) => {
                  if (!value || parseLngLat(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(i18n.t("merchantLocation.coordinateHint"))
                  );
                },
              },
            ]}
          >
            <Input
              placeholder="经度,纬度"
              onBlur={onCoordinateBlur}
            />
          </Form.Item>
          <div
            id="merchant-location-map"
            style={{ height: 420, width: "100%", marginBottom: 16 }}
          />
          <Paragraph type="secondary" style={{ textAlign: "center" }}>
            {i18n.t("merchantLocation.mapHint")}
          </Paragraph>
          <Form.Item wrapperCol={{ offset: 4 }}>
            <Button type="primary" htmlType="submit" size="large" loading={saving}>
              {i18n.t("sales.submit")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Spin>
  );
}

export default MerchantLocationSetting;
